"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

import { logoInputs } from "../Logo/inputs/logoInputs.builder";

import { softAgeVerificationButtonsComponentName } from "./components/BirthDateCtas";
import { SoftAgeVerificationBirthDateFormInputsName } from "./components/BirthDateFormInputs";
import { SoftAgeVerificationDisclaimerName } from "./components/Disclaimer/Disclaimer.builder";

export const softAgeVerificationInputs = [
  {
    name: "backgroundColor",
    type: "string",
    enum: [
      "white",
      "black",
      "softBlack",
      "blurredWhite",
      "blurredBlack",
      "blurredSoftBlack",
    ],
    required: true,
    defaultValue: "blurredWhite",
  },
  {
    name: "isLogoEnabled",
    type: "boolean",
    defaultValue: true,
  },
  {
    name: "shouldPageReload",
    type: "boolean",
    defaultValue: false,
    helperText: "Should reload page when Confirm button is clicked?",
  },
  {
    name: "logo",
    type: "object",
    subFields: logoInputs,
    required: true,
    defaultValue: {
      alt: "Logo",
      srcLight:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F1ee41566ad9d4c978d68fd13d262bcec",
      srcDark:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F9d1d2ffe26704533a8ad5fbf1abac751",
      title: "Logo",
      link: "/",
    },
  },
] as const satisfies Inputs;

export const SoftAgeVerificationComponent: RegisteredComponent = {
  name: "SoftAgeVerification",
  component: dynamic(
    async () =>
      withValidation(
        softAgeVerificationInputs,
        (await import("./SoftAgeVerification")).SoftAgeVerification,
        "SoftAgeVerification"
      ),
    { ssr: false }
  ),
  canHaveChildren: true,
  models: [getBuilderModelName("soft-age-verification")],
  inputs: softAgeVerificationInputs,
  childRequirements: {
    message: "Only Soft Age Verification related components are allowed",
    query: {
      "component.name": {
        $in: [
          SoftAgeVerificationDisclaimerName,
          SoftAgeVerificationBirthDateFormInputsName,
          softAgeVerificationButtonsComponentName,
        ],
      },
    },
  },
  defaultChildren: [
    {
      "@type": "@builder.io/sdk:Element",
      component: {
        name: SoftAgeVerificationDisclaimerName,
        options: {
          content:
            "<p>[Ploom domain.com] is limited to adult smokers. To access exclusive content and personalized experiences, please login with a Ploom account or continue as a guest via verifying your age via the state application</p>",
        },
      },
    },
    {
      "@type": "@builder.io/sdk:Element",
      component: {
        name: SoftAgeVerificationBirthDateFormInputsName,
        options: {
          headline: "Are you over 18?",
          description:
            "<p>You must be 18 years old to enter this site.</p><p>Please enter your birth date below.</p>",
          dayPlaceholder: "DD",
          monthPlaceholder: "MM",
          yearPlaceholder: "YYYY",
          validationMessage: "Please enter a valid date",
          minimumAge: 18,
          format: "dd/mm/yyyy",
          inputType: "inputs",
          isInputsHidden: false,
        },
      },
    },
    {
      "@type": "@builder.io/sdk:Element",
      component: {
        name: softAgeVerificationButtonsComponentName,
        options: {
          confirmLabel: "Confirm",
          leaveLabel: "Leave",
          leaveUrl: "https://www.google.com",
        },
      },
    },
  ],
};
